import { publicContactUsUrl } from "common/config";
import { useI18N } from "common/hooks";
import type { GuideUnavailableReason } from "common/apis/venueDetails";
import GuideUnavailableIllustration from "@/assets/images/guide-unavailable.svg";
import { standardButtonVariants } from "@/components/common/StandardButton";
import { cn } from "@/lib/utils";

export interface GuideUnavailablePageProps {
  /** Why the guide is not live — same copy for every reason (product decision, DEV-605). */
  reason: GuideUnavailableReason;
  /** Canonical slug of the parent venue; empty when the backend could not supply one. */
  venueSlug: string;
  venueName?: string;
}

/**
 * Full-page state for a custom-view guide link outside its live window (Figma 1063:199050).
 * Owns `<main id="main">` like every route here: the root layout's skip link and
 * route-change focus both target that id.
 */
function GuideUnavailablePage(props: GuideUnavailablePageProps) {
  void props;
  const i18n = useI18N();

  // The CTA is a real link styled as the primary button: a <button> nested inside
  // an <a> would be two interactive controls for one action.
  const ctaClassName = cn(
    standardButtonVariants({ variant: "primary", size: "md" }),
    "min-h-[48px] w-full max-w-[257px] px-3 py-3 focus-blue",
  );

  return (
    <main
      id="main"
      className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-10"
    >
      <img
        src={GuideUnavailableIllustration}
        alt=""
        aria-hidden="true"
        className="h-auto w-full max-w-[388px]"
      />

      <div className="flex w-full flex-col items-center gap-4 px-4 py-6">
        <div className="flex flex-col items-center text-center">
          <h1 className="title-l-bold text-fg-neutral-strong">
            {i18n.get("guideUnavailableTitle")}
          </h1>
          <p className="body-base-medium text-fg-neutral-subtle max-w-[466px]">
            {i18n.get("guideUnavailableLine1")}
            <br />
            {i18n.get("guideUnavailableLine2")}
          </p>
        </div>

        <a
          href={publicContactUsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={ctaClassName}
        >
          {i18n.get("guideUnavailableCta")}
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </div>
    </main>
  );
}

export default GuideUnavailablePage;
